import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { CallistoLogo } from "../components/CallistoLogo";
import { useAuth } from "../context/AuthContext";
import { Loader2, UserPlus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  sales_officer: "Sales Officer",
  assistant_manager: "Sales Manager",
  admin_manager: "Admin Manager",
  admin: "Tech Admin",
  director: "Director",
};

export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, setUser } = useAuth();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", phone: "", password: "", password2: "" });
  const [submitting, setSubmitting] = useState(false);   const [showPw, setShowPw] = useState(false);   const [showPw2, setShowPw2] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invite link is invalid.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.password2) {
      setError("Passwords do not match.");
      return;
    }
    if (!form.full_name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/invite/accept", {
        token,
        password: form.password,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
      });
      if (data?.access_token) {
        localStorage.setItem("callisto_token", data.access_token);
      }
      setUser(data.user);
      toast.success(`Welcome aboard, ${data.user.full_name.split(" ")[0]}!`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-callisto-app">
      <div className="px-6 py-5 bg-white border-b border-callisto">
        <CallistoLogo variant="light" size="md" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-callisto shadow-sm p-8">
          {loading ? (
            <div className="flex items-center gap-2 text-callisto-muted text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Validating invite…
            </div>
          ) : error && !invite ? (
            <div className="text-center">
              <h1 className="text-xl font-bold text-callisto-navy">Invite unavailable</h1>
              <p className="mt-2 text-sm text-callisto-muted">{error}</p>
              <Link
                to="/login"
                className="mt-5 inline-block text-sm font-semibold text-callisto-gold hover:underline"
              >
                Go to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-xl bg-callisto-gold/10 flex items-center justify-center mb-4">
                <UserPlus className="h-6 w-6 text-callisto-gold" />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-callisto-gold">
                You've been invited
              </span>
              <h1 className="mt-2 text-2xl font-bold text-callisto-navy">
                Join Callisto SFA
              </h1>
              <p className="mt-2 text-sm text-callisto-muted">
                <span className="font-semibold text-callisto-navy">{invite?.email}</span> ·{" "}
                Role: <span className="font-semibold text-callisto-navy">{ROLE_LABEL[invite?.role] || invite?.role}</span>
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="invite-accept-form">
                <div className="space-y-2">
                  <Label htmlFor="inv-name" className="text-callisto-navy font-medium">
                    Full name
                  </Label>
                  <Input
                    id="inv-name"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                    data-testid="invite-accept-fullname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-phone" className="text-callisto-navy font-medium">
                    Phone <span className="text-callisto-muted font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="inv-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 …"
                    className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                    data-testid="invite-accept-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-pw" className="text-callisto-navy font-medium">
                    Password
                  </Label>
   <div className="relative">
                  <Input
                    id="inv-pw"
                    type={showPw ? "text" : "password"}
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0 pr-10"
                    data-testid="invite-accept-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-callisto-muted">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>                
                <div className="space-y-2">
                  <Label htmlFor="inv-pw2" className="text-callisto-navy font-medium">
                    Confirm password
                  </Label>
<div className="relative">
                  <Input
                    id="inv-pw2"
                    type={showPw2 ? "text" : "password"}
                    minLength={8}
                    value={form.password2}
                    onChange={(e) => setForm({ ...form, password2: e.target.value })}
                    required
                    className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0 pr-10"
                    data-testid="invite-accept-password-confirm-input"
                  />
                  <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute right-3 top-3 text-callisto-muted">
                    {showPw2 ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
             </div>
              {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
                  data-testid="invite-accept-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
